import { useEffect, useRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/theme-provider";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import {
  SendIcon,
  LoaderIcon,
  Sparkles,
  Handshake,
  Clapperboard,
  Target,
  TrendingUp,
  Film,
  Lightbulb,
  Youtube,
  ShoppingBag,
  Rocket,
  Check,
  X,
  ArrowLeft,
  Users,
  ChevronDown,
  Palette,
  Maximize2,
  Minimize2,
  ShieldCheck,
} from "lucide-react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { TextRotate } from "@/components/ui/text-rotate";
import { apiRequest, apiRequestSSE } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useStore } from "@/lib/store";
import AdGraphicsBuilder from "@/components/ad-graphics-builder";
import { ReadinessPanel } from "@/components/campaign-readiness-panel";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// ─── Campaign Progress Bar ───────────────────────────────────────────────────
const CAMPAIGN_STEPS = [
  { num: 1, labelKey: "campaignSteps.1" },
  { num: 2, labelKey: "campaignSteps.2" },
  { num: 3, labelKey: "campaignSteps.3" },
  { num: 4, labelKey: "campaignSteps.4" },
  { num: 5, labelKey: "campaignSteps.5" },
  { num: 6, labelKey: "campaignSteps.6" },
];

function CampaignProgressBar({ currentStep }: { currentStep: number }) {
  const { t } = useTranslation("animatedWelcomeChat");
  const progress = ((currentStep - 1) / (CAMPAIGN_STEPS.length - 1)) * 100;

  return (
    <div className="px-6 pb-3">
      {/* Step labels */}
      <div className="flex items-center justify-between mb-2">
        {CAMPAIGN_STEPS.map((step) => {
          const isDone = step.num < currentStep;
          const isActive = step.num === currentStep;
          return (
            <div
              key={step.num}
              className="flex flex-col items-center gap-1"
              style={{ width: `${100 / CAMPAIGN_STEPS.length}%` }}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-500 ${
                  isDone
                    ? "bg-[#ef3e29] text-white shadow-[0_0_8px_rgba(239,62,41,0.5)]"
                    : isActive
                      ? "bg-[#ef3e29]/30 border-2 border-[#ef3e29] text-[#ef3e29]"
                      : "bg-white/[0.06] border border-white/[0.12] text-white/30"
                }`}
              >
                {isDone ? <Check className="w-3 h-3" /> : step.num}
              </div>
              <span
                className={`text-[9px] font-medium leading-none transition-all duration-300 text-center ${
                  isDone ? "text-[#ef3e29]/80" : isActive ? "text-white" : "text-white/25"
                }`}
              >
                {t(step.labelKey)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Progress track */}
      <div className="relative h-1 bg-white/[0.06] rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-600 to-orange-500 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        />
        {/* Glow dot at head */}
        {progress > 0 && (
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-orange-400 rounded-full shadow-[0_0_6px_rgba(251,146,60,0.8)]"
            animate={{ left: `calc(${progress}% - 4px)` }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          />
        )}
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

interface UseAutoResizeTextareaProps {
  minHeight: number;
  maxHeight?: number;
}

function useAutoResizeTextarea({ minHeight, maxHeight }: UseAutoResizeTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(
    (reset?: boolean) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      if (reset) {
        textarea.style.height = `${minHeight}px`;
        return;
      }

      textarea.style.height = `${minHeight}px`;
      const newHeight = Math.max(
        minHeight,
        Math.min(textarea.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY)
      );
      textarea.style.height = `${newHeight}px`;
    },
    [minHeight, maxHeight]
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = `${minHeight}px`;
    }
  }, [minHeight]);

  return { textareaRef, adjustHeight };
}

interface ModuleSuggestion {
  icon: React.ReactNode;
  labelKey: string;
  prompt: string;
  moduleId: string;
}

const MODULE_SUGGESTIONS: ModuleSuggestion[] = [
  {
    icon: <Rocket className="w-4 h-4" />,
    labelKey: "moduleSuggestions.campaigns",
    prompt: "Stwórz kampanię marketingową",
    moduleId: "campaign-creator",
  },
  {
    icon: <Handshake className="w-4 h-4" />,
    labelKey: "moduleSuggestions.createOffer",
    prompt: "Stwórz ofertę premium dla mojego produktu",
    moduleId: "offer-builder",
  },
  {
    icon: <Clapperboard className="w-4 h-4" />,
    labelKey: "moduleSuggestions.writeVsl",
    prompt: "Napisz skrypt VSL dla mojej oferty",
    moduleId: "vsl-creator",
  },
  {
    icon: <Target className="w-4 h-4" />,
    labelKey: "moduleSuggestions.createAd",
    prompt: "Stwórz reklamę na Facebooka dla mojego produktu",
    moduleId: "ad-creator",
  },
  {
    icon: <TrendingUp className="w-4 h-4" />,
    labelKey: "moduleSuggestions.analyzeCampaigns",
    prompt: "Pokaż mi analizę moich kampanii reklamowych",
    moduleId: "marketing-analyst",
  },
  {
    icon: <Film className="w-4 h-4" />,
    labelKey: "moduleSuggestions.createReels",
    prompt: "Stwórz skrypt na Reels",
    moduleId: "reel-creator",
  },
  {
    icon: <Lightbulb className="w-4 h-4" />,
    labelKey: "moduleSuggestions.ideaBank",
    prompt: "",
    moduleId: "idea-bank",
  },
  {
    icon: <Youtube className="w-4 h-4" />,
    labelKey: "moduleSuggestions.youtubeMode",
    prompt: "Pomóż mi stworzyć film na YouTube",
    moduleId: "youtube-mode",
  },
  {
    icon: <ShoppingBag className="w-4 h-4" />,
    labelKey: "moduleSuggestions.allegroAds",
    prompt: "",
    moduleId: "allegro-ads",
  },
];

interface AnimatedWelcomeChatProps {
  username: string;
  onSubmit: (text: string) => void;
  onModuleSelect?: (moduleId: string, prompt: string) => void;
  availableModuleIds: string[];
}

// ─── Campaign option parser ───
type CampaignOption = { id: string; label: string };

function parseCampaignOptions(content: string): {
  cleanContent: string;
  options: CampaignOption[];
} {
  const options: CampaignOption[] = [];
  let cleanContent = content.replace(
    /\[OPTIONS\]\s*([\s\S]*?)\s*\[\/OPTIONS\]/g,
    (_, block: string) => {
      block
        .trim()
        .split("\n")
        .forEach((line) => {
          const trimmed = line.trim();
          if (!trimmed) return;
          const pipeIndex = trimmed.indexOf("|");
          if (pipeIndex > 0) {
            options.push({
              id: trimmed.slice(0, pipeIndex).trim(),
              label: trimmed.slice(pipeIndex + 1).trim(),
            });
          } else {
            options.push({ id: trimmed, label: trimmed });
          }
        });
      return "";
    }
  );
  cleanContent = cleanContent.replace(/\[SAVE_KPI\][\s\S]*?\[\/SAVE_KPI\]/g, "");
  cleanContent = cleanContent.replace(/\[SAVE_OFFER\][\s\S]*?\[\/SAVE_OFFER\]/g, "");
  return { cleanContent: cleanContent.trim(), options };
}

// ─── Build a focused ad-graphics prompt from KROK 4 message + offer sheet ───
function buildCampaignAdPrompt(stepMessage: string, offerTitle?: string | null): string {
  const clean = parseCampaignOptions(stepMessage).cleanContent;
  // Strip markdown headings/quotes for cleaner prompt
  const stripped = clean
    .replace(/^#+\s+/gm, "")
    .replace(/^>\s+/gm, "")
    .trim();
  const offerLine = offerTitle ? `\n\nKontekst oferty: ${offerTitle}` : "";
  return (stripped + offerLine).slice(0, 1200);
}

interface ParsedKpi {
  businessModel?: "ecommerce_direct" | "consultation_booking" | "lead_collection";
  monthlyBudget?: number;
  monthlyLeadsMin?: number;
  monthlyLeadsMax?: number;
  monthlyPurchasesMin?: number;
  monthlyPurchasesMax?: number;
  targetCplMin?: number;
  targetCplMax?: number;
  targetCpaMin?: number;
  targetCpaMax?: number;
  targetRoas?: number;
}

function parseKpiBlock(content: string): ParsedKpi | null {
  const match = content.match(/\[SAVE_KPI\]([\s\S]*?)\[\/SAVE_KPI\]/);
  if (!match) return null;
  const block = match[1];

  const num = (key: string): number | undefined => {
    const m = block.match(new RegExp(`${key}:\\s*([\\d.]+)`));
    return m ? parseFloat(m[1]) : undefined;
  };

  const bmMatch = block.match(/(?:business_model|MODEL)\s*:\s*([a-z_]+)/i);
  const bm = bmMatch?.[1]?.toLowerCase();
  const businessModel: ParsedKpi["businessModel"] =
    bm === "ecommerce_direct" || bm === "consultation_booking" || bm === "lead_collection"
      ? bm
      : undefined;

  return {
    businessModel,
    monthlyBudget: num("monthly_budget"),
    monthlyLeadsMin: num("monthly_leads_min"),
    monthlyLeadsMax: num("monthly_leads_max"),
    monthlyPurchasesMin: num("monthly_purchases_min"),
    monthlyPurchasesMax: num("monthly_purchases_max"),
    targetCplMin: num("target_cpl_min"),
    targetCplMax: num("target_cpl_max"),
    targetCpaMin: num("target_cpa_min"),
    targetCpaMax: num("target_cpa_max"),
    targetRoas: num("target_roas"),
  };
}

type ChatMsg = { role: "user" | "assistant"; content: string; phase?: "offer" | "goals" | "main" };

type AgencyClient = {
  id: string;
  name: string;
  userId?: string | null;
  businessType: string;
  offerSheetId?: string | null;
};

export function AnimatedWelcomeChat({
  username,
  onSubmit,
  onModuleSelect,
  availableModuleIds,
}: AnimatedWelcomeChatProps) {
  const { resolvedTheme } = useTheme();
  const L = resolvedTheme === "light";
  const { t } = useTranslation("animatedWelcomeChat");
  const { user } = useAuth();
  const [value, setValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [strategyResult, setStrategyResult] = useState<string | null>(null);
  const [isLoadingStrategy, setIsLoadingStrategy] = useState(false);
  const [routeResult, setRouteResult] = useState<{
    moduleId: string;
    moduleName: string;
    suggestion: string;
    originalPrompt: string;
  } | null>(null);

  // ─── Inline campaign chat state ───
  const [campaignChatActive, setCampaignChatActive] = useState(false);
  const [campaignMessages, setCampaignMessages] = useState<ChatMsg[]>([]);
  const [campaignConvId, setCampaignConvId] = useState<string | null>(null);
  const [offerConvId, setOfferConvId] = useState<string | null>(null);
  const [goalsConvId, setGoalsConvId] = useState<string | null>(null);
  // 'offer' = offer-builder (krok 1), 'goals' = goals-builder (krok 2), 'main' = campaign-creator (kroki 3-6)
  const [campaignPhase, setCampaignPhase] = useState<"offer" | "goals" | "main">("offer");
  const [savedOfferIndices, setSavedOfferIndices] = useState<Set<number>>(new Set());
  const [savedKpiIndices, setSavedKpiIndices] = useState<Set<number>>(new Set());
  const [pendingInitialGoals, setPendingInitialGoals] = useState<Record<string, unknown> | null>(
    null
  );
  const [campaignBusinessModel, setCampaignBusinessModel] = useState<
    "ecommerce_direct" | "consultation_booking" | "lead_collection" | undefined
  >(undefined);
  const [expressUrl, setExpressUrl] = useState("");
  const [expressLoading, setExpressLoading] = useState(false);
  const [expressError, setExpressError] = useState<string | null>(null);
  const [expressDismissed, setExpressDismissed] = useState(false);
  const [expressOfferSheetId, setExpressOfferSheetId] = useState<string | null>(null);
  const [referencePhotos] = useState<{ name: string; dataUrl: string }[]>([]);
  const [isCampaignSending, setIsCampaignSending] = useState(false);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [detectedCampaignStep, setDetectedCampaignStep] = useState(1);
  const [campaignDraftId, setCampaignDraftId] = useState<string | null>(null);
  const [draftReadiness, setDraftReadiness] = useState<{
    ready: boolean;
    missing: import("./campaign-readiness-panel").MissingItem[];
  } | null>(null);
  const [isLaunchingCampaign, setIsLaunchingCampaign] = useState(false);
  const [metaConnectionWarning, setMetaConnectionWarning] = useState(false);
  const [isAutopiloting, setIsAutopiloting] = useState(false);
  const [graphicsBuilderFullScreen, setGraphicsBuilderFullScreen] = useState(false);
  const campaignScrollRef = useRef<HTMLDivElement>(null);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  // ─── Agency client from global store ───
  const {
    clients: globalClients,
    selectedClientId: globalSelectedClientId,
    selectedClient: globalSelectedClient,
    setSelectedClient: setGlobalSelectedClient,
    isAgencyUser,
  } = useAgencyClients();
  const selectedClientId = globalSelectedClientId ?? "none";
  const myClients = globalClients;
  const selectedClient = globalClients.find((c) => c.id === selectedClientId);

  // Fetch offer sheet for selected client
  const { data: clientOfferSheetData } = useQuery<{
    offerSheet: { id: string; title: string } | null;
  }>({
    queryKey: ["client-offer-sheet", selectedClientId],
    queryFn: () => apiRequest("GET", `/api/my-clients/${selectedClientId}/offer-sheet`),
    enabled: selectedClientId !== "none" && isAgencyUser,
    staleTime: 60_000,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target as Node)) {
        setIsClientDropdownOpen(false);
      }
    };
    if (isClientDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isClientDropdownOpen]);

  const { textareaRef, adjustHeight } = useAutoResizeTextarea({
    minHeight: 56,
    maxHeight: 200,
  });

  const campaignInputRef = useRef<HTMLTextAreaElement>(null);

  const availableModules = new Set(availableModuleIds);
  // VP-103: "idea-bank" (Bank pomysłów) removed — hide its welcome-screen shortcut too.
  const hiddenModules = new Set(["clone-yourself", "email-creator", "idea-bank"]);

  const visibleSuggestions = MODULE_SUGGESTIONS.filter(
    (s) => availableModules.has(s.moduleId) && !hiddenModules.has(s.moduleId)
  );

  const isEmailFormat = username.includes("@");
  const displayName = isEmailFormat
    ? username
        .split("@")[0]
        .split(/[._-]/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : username;

  // Auto-scroll campaign chat
  useEffect(() => {
    if (campaignScrollRef.current) {
      campaignScrollRef.current.scrollTop = campaignScrollRef.current.scrollHeight;
    }
  }, [campaignMessages, isCampaignSending]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        handleSend();
      }
    }
  };

  const handleCampaignKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        sendCampaignMessage(value.trim());
      }
    }
  };

  // ─── Send message — routes to offer-builder | goals-builder | campaign-creator depending on phase ───
  const sendCampaignMessage = async (text: string, forcePhase?: "offer" | "goals" | "main") => {
    if (!text.trim() || isCampaignSending) return;
    setValue("");
    adjustHeight(true);
    setIsCampaignSending(true);

    const phase = forcePhase ?? campaignPhase;
    setCampaignMessages((prev) => [...prev, { role: "user", content: text }]);

    try {
      let assistantContent = "";

      if (phase === "offer") {
        // ── Offer-builder endpoint (Krok 1) ──
        let convId = offerConvId;
        if (!convId) {
          const conv = await apiRequest("POST", "/api/offer-builder/conversations", {
            title: "Oferta kampanii",
          });
          convId = conv.id;
          setOfferConvId(convId);
        }
        const offerPayload: Record<string, unknown> = {
          content: text,
          businessType: selectedClient?.businessType === "local" ? "local" : "online",
        };
        if (selectedClientId !== "none") {
          offerPayload.clientId = selectedClientId;
        } else if (clientOfferSheetData?.offerSheet?.id) {
          offerPayload.offerSheetId = clientOfferSheetData.offerSheet.id;
        }
        // VP-144: offer-builder messages stream over SSE (heartbeats avoid 504 on
        // long generations). Same body shape as the old JSON response.
        const resp = await apiRequestSSE(
          "POST",
          `/api/offer-builder/conversations/${convId}/messages`,
          offerPayload,
          { signal: AbortSignal.timeout(120_000) }
        );
        assistantContent = resp.assistantMessage?.content || resp.aiMessage?.content || "";
        setCampaignMessages((prev) => [
          ...prev,
          { role: "assistant", content: assistantContent, phase: "offer" },
        ]);
      } else if (phase === "goals") {
        // ── Goals-builder endpoint (Krok 2 — Cel & KPI) ──
        let convId = goalsConvId;
        if (!convId) {
          const conv = await apiRequest("POST", "/api/goals-builder/conversations", {
            title: "Cele kampanii",
          });
          convId = conv.id;
          setGoalsConvId(convId);
        }
        const goalsPayload: Record<string, unknown> = { content: text };
        if (selectedClientId !== "none") {
          goalsPayload.clientId = selectedClientId;
        } else if (clientOfferSheetData?.offerSheet?.id) {
          goalsPayload.offerSheetId = clientOfferSheetData.offerSheet.id;
        }
        const resp = await apiRequest(
          "POST",
          `/api/goals-builder/conversations/${convId}/messages`,
          goalsPayload,
          { signal: AbortSignal.timeout(120_000) }
        );
        assistantContent = resp.assistantMessage?.content || resp.aiMessage?.content || "";

        const newMsgIdx = campaignMessages.length + 1;
        setCampaignMessages((prev) => [
          ...prev,
          { role: "assistant", content: assistantContent, phase: "goals" },
        ]);

        // Auto-save KPI if response contains [SAVE_KPI] block
        const parsedKpi = parseKpiBlock(assistantContent);
        if (parsedKpi) {
          saveKpiTargets(parsedKpi, newMsgIdx);
        }

        // Capture server-parsed goals (incl. businessModel) for the first campaign-creator request
        const serverParsedGoals = (resp as { meta?: { parsedGoals?: Record<string, unknown> } })
          .meta?.parsedGoals;
        if (serverParsedGoals && Object.keys(serverParsedGoals).length > 0) {
          setPendingInitialGoals(serverParsedGoals);
          const bm = (serverParsedGoals as { businessModel?: string }).businessModel;
          if (
            bm === "ecommerce_direct" ||
            bm === "consultation_booking" ||
            bm === "lead_collection"
          ) {
            setCampaignBusinessModel(bm);
          }
        }
      } else {
        // ── Campaign-creator endpoint (Kroki 2-6) ──
        let convId = campaignConvId;
        if (!convId) {
          const conv = await apiRequest("POST", "/api/campaign-creator/conversations", {
            title: "Kampania",
          });
          convId = conv.id;
          setCampaignConvId(convId);
        }
        const campaignPayload: Record<string, unknown> = {
          content: text,
          campaignStep: "briefing",
        };
        if (selectedClientId !== "none") {
          campaignPayload.clientId = selectedClientId;
        } else if (expressOfferSheetId) {
          campaignPayload.offerSheetId = expressOfferSheetId;
        } else if (clientOfferSheetData?.offerSheet?.id) {
          campaignPayload.offerSheetId = clientOfferSheetData.offerSheet.id;
        }
        if (pendingInitialGoals && Object.keys(pendingInitialGoals).length > 0) {
          campaignPayload.initialGoals = pendingInitialGoals;
        }
        const resp = await apiRequest(
          "POST",
          `/api/campaign-creator/conversations/${convId}/messages`,
          campaignPayload,
          { signal: AbortSignal.timeout(120_000) }
        );
        if (pendingInitialGoals) setPendingInitialGoals(null);
        assistantContent = resp.assistantMessage?.content || resp.aiMessage?.content || "";

        const newMsgIdx = campaignMessages.length + 1; // +1 for the user message just added
        setCampaignMessages((prev) => [
          ...prev,
          { role: "assistant", content: assistantContent, phase: "main" },
        ]);

        // Backend-driven step (replaces fragile regex on LLM text)
        const meta = resp.meta as { currentStep?: number; draftId?: string } | undefined;
        if (meta?.currentStep && meta.currentStep >= 1 && meta.currentStep <= 6) {
          setDetectedCampaignStep((prev) => Math.max(prev, meta.currentStep!));
        }
        if (meta?.draftId) setCampaignDraftId(meta.draftId);

        // Auto-save KPI if response contains [SAVE_KPI] block (legacy fallback)
        const parsedKpi = parseKpiBlock(assistantContent);
        if (parsedKpi) {
          saveKpiTargets(parsedKpi, newMsgIdx);
        }
      }
    } catch (err) {
      console.error("[CAMPAIGN-CHAT] sendCampaignMessage error", err);
      const detail = err instanceof Error ? err.message : "nieznany błąd";
      setCampaignMessages((prev) => [
        ...prev,
        { role: "assistant", content: t("systemMessages.sorryError", { detail }), phase: "main" },
      ]);
    } finally {
      setIsCampaignSending(false);
    }
  };

  // ─── Switch from offer phase → goals phase (or skip to main if KPI already set) ───
  const handleSwitchToCampaignPhase = async () => {
    try {
      const { hasKpi } = await detectExistingAssets();
      if (hasKpi) {
        setCampaignPhase("main");
        setDetectedCampaignStep(3);
        sendCampaignMessage(
          "Mam ofertę i ustalone KPI dla tego klienta. Zaprojektuj strukturę kampanii (Krok 3) — ad sety, targetowanie i wstępne kreacje.",
          "main"
        );
        return;
      }
    } catch {
      // fallback do goals
    }
    setCampaignPhase("goals");
    setDetectedCampaignStep(2);
    sendCampaignMessage(
      "Oferta gotowa. Przejdź do Kroku 2 — ustalmy cele biznesowe i KPI dla kampanii.",
      "goals"
    );
  };

  // ─── Switch from goals phase → main (Krok 3 — Struktura kampanii) ───
  const handleSwitchToStructurePhase = () => {
    setCampaignPhase("main");
    setDetectedCampaignStep(3);
    sendCampaignMessage(
      "KPI ustawione. Przejdź do Kroku 3 — zaprojektujmy strukturę kampanii (ad sety i reklamy).",
      "main"
    );
  };

  // ─── Save offer content to Arkusz Ofert ───
  const handleSaveOffer = async (content: string, msgIdx: number) => {
    try {
      const title = `Oferta kampanii — ${selectedClient?.name || new Date().toLocaleDateString("pl-PL")}`;
      if (selectedClientId !== "none") {
        await apiRequest("PUT", `/api/my-clients/${selectedClientId}/offer-sheet`, {
          title,
          content,
        });
      } else {
        await apiRequest("POST", "/api/offer-sheets", { title, content });
      }
      setSavedOfferIndices((prev) => new Set(prev).add(msgIdx));
    } catch {
      // silent — user can retry
    }
  };

  // ─── Save KPI targets to analytics-settings ───
  const saveKpiTargets = async (kpi: ParsedKpi, msgIdx: number) => {
    try {
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const quarter = Math.ceil((now.getMonth() + 1) / 3);
      const quarterKey = `${now.getFullYear()}-Q${quarter}`;

      const isEcom = kpi.businessModel === "ecommerce_direct";

      // For e-commerce the "leads" slot is repurposed as monthly purchases (monitoring uses one column)
      const targetLeads = isEcom
        ? kpi.monthlyPurchasesMin != null && kpi.monthlyPurchasesMax != null
          ? Math.round((kpi.monthlyPurchasesMin + kpi.monthlyPurchasesMax) / 2)
          : (kpi.monthlyPurchasesMin ?? kpi.monthlyPurchasesMax)
        : kpi.monthlyLeadsMin != null && kpi.monthlyLeadsMax != null
          ? Math.round((kpi.monthlyLeadsMin + kpi.monthlyLeadsMax) / 2)
          : (kpi.monthlyLeadsMin ?? kpi.monthlyLeadsMax);

      // monitoring-service treats `targetCpl` as CPA cap when business is ecommerce
      const targetCpl = isEcom
        ? kpi.targetCpaMin != null && kpi.targetCpaMax != null
          ? Math.round((kpi.targetCpaMin + kpi.targetCpaMax) / 2)
          : (kpi.targetCpaMin ?? kpi.targetCpaMax)
        : kpi.targetCplMin != null && kpi.targetCplMax != null
          ? Math.round((kpi.targetCplMin + kpi.targetCplMax) / 2)
          : (kpi.targetCplMin ?? kpi.targetCplMax);

      const monthlyTargets = {
        [monthKey]: {
          targetLeads: targetLeads ?? null,
          targetCpl: targetCpl ?? null,
          targetRoas: kpi.targetRoas ?? null,
          budget: kpi.monthlyBudget ?? null,
        },
      };

      const quarterlyTargets = {
        [quarterKey]: {
          targetLeads: targetLeads != null ? targetLeads * 3 : null,
          targetCpl: targetCpl ?? null,
          targetRoas: kpi.targetRoas ?? null,
        },
      };

      const body: Record<string, unknown> = { monthlyTargets, quarterlyTargets };
      if (selectedClientId !== "none" && selectedClient?.userId) {
        body.forClientUserId = selectedClient.userId;
      }

      await apiRequest("PATCH", "/api/analytics-settings/targets", body);
      setSavedKpiIndices((prev) => new Set(prev).add(msgIdx));
    } catch {
      // silent — user can retry manually
    }
  };

  // ─── Preflight: check Meta connection before starting campaign chat ───
  const checkMetaConnection = async (): Promise<boolean> => {
    try {
      const status = await apiRequest("GET", "/api/integrations/meta/status");
      return Boolean(status?.connected);
    } catch {
      return false;
    }
  };

  // ─── Detect existing assets (offer sheet + KPI) for the selected client/user ───
  const detectExistingAssets = async (): Promise<{
    offer: { id: string; title: string } | null;
    hasKpi: boolean;
  }> => {
    const isClient = selectedClientId !== "none";
    const settingsUrl = isClient
      ? `/api/analytics-settings?forClientUserId=${selectedClientId}`
      : "/api/analytics-settings";

    const offerUrl = isClient ? `/api/my-clients/${selectedClientId}/offer-sheet` : null;

    const [offerResp, settingsResp] = await Promise.all([
      offerUrl ? apiRequest("GET", offerUrl).catch(() => null) : Promise.resolve(null),
      apiRequest("GET", settingsUrl).catch(() => null),
    ]);

    const offer = offerResp?.offerSheet ?? null;
    const monthlyTargets = settingsResp?.monthlyTargets ?? {};
    const kpiValues = Object.values(monthlyTargets) as Array<Record<string, unknown> | null>;
    const hasKpi = kpiValues.some(
      (v) => v != null && (v.targetLeads != null || v.targetCpl != null || v.targetRoas != null)
    );

    return { offer, hasKpi };
  };

  // ─── Build greeting message that respects existing assets ───
  const buildIntroMessage = (offer: { title: string } | null, hasKpi: boolean): string => {
    const clientLabel = selectedClient?.name ? ` dla **${selectedClient.name}**` : "";

    if (offer && hasKpi) {
      return [
        `Widzę, że${clientLabel} mamy już gotowy arkusz ofertowy **„${offer.title}"** oraz ustalone cele KPI.`,
        "",
        "Co robimy?",
        "",
        "[OPTIONS]",
        "use_existing_all|✅ Lecimy z tym — przejdź od razu do struktury kampanii (Krok 3)",
        "use_existing_offer|🎯 Oferta OK, ale zaktualizujmy KPI",
        "start_fresh|🆕 Zbuduj wszystko od nowa",
        "[/OPTIONS]",
      ].join("\n");
    }

    if (offer && !hasKpi) {
      return [
        `Widzę, że${clientLabel} mamy już arkusz ofertowy **„${offer.title}"**, ale brakuje ustalonych KPI.`,
        "",
        "Co robimy?",
        "",
        "[OPTIONS]",
        "use_existing_offer|✅ Używam tej oferty — przejdźmy do ustalenia KPI (Krok 2)",
        "start_fresh|🆕 Zbuduj nową ofertę od zera",
        "[/OPTIONS]",
      ].join("\n");
    }

    if (!offer && hasKpi) {
      return [
        `Widzę${clientLabel} ustalone cele KPI, ale nie ma jeszcze arkusza ofertowego.`,
        "",
        "Zaczynamy od zbudowania oferty — opowiedz mi pokrótce: co sprzedajemy, do kogo i jaki jest realny budżet kampanii?",
      ].join("\n");
    }

    // Brak obu — standardowy onboarding offer-builder
    return "";
  };

  // ─── Start inline campaign chat ───
  const startCampaignChat = async (initialMessage: string) => {
    setCampaignChatActive(true);
    setRouteResult(null);
    setStrategyResult(null);
    setValue("");
    const connected = await checkMetaConnection();
    setMetaConnectionWarning(!connected);

    // Smart-skip: jeśli klient ma już ofertę i/lub KPI, zaproponuj reuse
    try {
      const { offer, hasKpi } = await detectExistingAssets();
      const intro = buildIntroMessage(offer, hasKpi);
      if (intro) {
        setCampaignMessages([{ role: "assistant", content: intro, phase: "offer" }]);
        return;
      }
    } catch {
      // fallback do klasycznej ścieżki offer-builder
    }

    // Beginner-friendly welcome — Krysia sees what's about to happen and clicks "start"
    setCampaignMessages([
      {
        role: "assistant",
        content: t("campaignChat.welcomeMessage"),
        phase: "offer",
      },
    ]);
  };

  // ─── Express init: paste URL, AI does scrape + offer sheet + KPI in one shot ───
  const runExpressInit = async () => {
    const url = expressUrl.trim();
    if (!url || expressLoading) return;
    if (!/^https?:\/\//i.test(url)) {
      setExpressError(t("campaignChat.expressErrorInvalidUrl"));
      return;
    }
    setExpressLoading(true);
    setExpressError(null);
    try {
      const expressBody: Record<string, unknown> = { url, persistOfferSheet: true };
      if (selectedClientId !== "none") expressBody.clientId = selectedClientId;
      const resp = await apiRequest("POST", "/api/campaign-creator/express-init", expressBody);
      const businessModel = resp.businessModel as
        | "ecommerce_direct"
        | "consultation_booking"
        | "lead_collection";
      const goals = resp.suggestedGoals as Record<string, unknown>;

      setCampaignBusinessModel(businessModel);
      setPendingInitialGoals(goals);
      if (resp.offerSheetId) setExpressOfferSheetId(String(resp.offerSheetId));

      const offerSummary = (resp.offerSheetContent as string | undefined)?.slice(0, 400) || "";
      const offerPreview = offerSummary
        ? `${t("campaignChat.expressOfferPreviewLabel")}\n\n${offerSummary}…\n\n`
        : "";
      const offerSheetIdSuffix = resp.offerSheetId
        ? ` (ID: ${String(resp.offerSheetId).slice(0, 8)}…)`
        : "";
      const intro = t("campaignChat.expressResultIntro", {
        businessModel,
        imageCount: (resp.productImageUrls ?? []).length,
        offerSheetIdSuffix,
        offerPreview,
      });

      setCampaignMessages([{ role: "assistant", content: intro, phase: "goals" }]);
      setCampaignPhase("goals");
      setExpressDismissed(true);
      setExpressUrl("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("campaignChat.expressErrorGeneric");
      setExpressError(msg);
    } finally {
      setExpressLoading(false);
    }
  };

  // ─── Refresh readiness from backend ───
  const refreshReadiness = async (draftId: string) => {
    try {
      const resp = await apiRequest("GET", `/api/campaign-creator/launch-readiness/${draftId}`);
      setDraftReadiness({ ready: Boolean(resp?.ready), missing: resp?.missing ?? [] });
    } catch {
      // silent
    }
  };

  // ─── Launch from draft (real Meta launch) ───
  const launchCampaignFromDraft = async () => {
    if (!campaignDraftId || isLaunchingCampaign) return;
    setIsLaunchingCampaign(true);
    try {
      const prep = await apiRequest(
        "POST",
        `/api/campaign-creator/launch-from-draft/${campaignDraftId}`
      );
      if (!prep?.launchBody) {
        setCampaignMessages((prev) => [
          ...prev,
          { role: "assistant", content: t("systemMessages.launchFailed"), phase: "main" },
        ]);
        return;
      }
      // Fire SSE-style POST to existing /launch endpoint
      await apiRequest("POST", "/api/campaign-creator/launch", prep.launchBody);
      setCampaignMessages((prev) => [
        ...prev,
        { role: "assistant", content: t("systemMessages.campaignLaunched"), phase: "main" },
      ]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd uruchomienia kampanii.";
      setCampaignMessages((prev) => [
        ...prev,
        { role: "assistant", content: `❌ ${msg}`, phase: "main" },
      ]);
    } finally {
      setIsLaunchingCampaign(false);
    }
  };

  // ─── Handlers map for option buttons (replaces else-if chain) ───
  const buildOptionHandler = (optId: string): (() => void | Promise<void>) | undefined => {
    const autopilotHandler = (preset: "broad" | "precise" | "local") => () => runAutopilot(preset);
    const handlers: Record<string, () => void | Promise<void>> = {
      go_to_offer_builder: () => {
        if (onModuleSelect)
          onModuleSelect("offer-builder", "Stwórz arkusz ofertowy dla mojego klienta");
      },
      launch: () => launchCampaignFromDraft(),
      connect_meta: () => {
        if (onModuleSelect) onModuleSelect("settings", "Połącz Meta");
      },
      autopilot_broad: autopilotHandler("broad"),
      autopilot_precise: autopilotHandler("precise"),
      autopilot_local: autopilotHandler("local"),
      // Smart-skip — istniejąca oferta + KPI: skacz od razu do struktury
      use_existing_all: () => {
        setCampaignPhase("main");
        setDetectedCampaignStep(3);
        sendCampaignMessage(
          "Lecimy z istniejącą ofertą i ustalonymi KPI. Zaprojektuj strukturę kampanii (Krok 3) — ile ad setów, jakie targetowanie i jakie wstępne kreacje proponujesz?",
          "main"
        );
      },
      // Smart-skip — istniejąca oferta, brakuje KPI: idziemy do goals-builder
      use_existing_offer: () => {
        setCampaignPhase("goals");
        setDetectedCampaignStep(2);
        sendCampaignMessage(
          "Mam już gotową ofertę. Pomóż mi ustalić cele biznesowe i KPI dla tej kampanii.",
          "goals"
        );
      },
      // Smart-skip — start od zera mimo istniejących assetów
      start_fresh: () => {
        setCampaignPhase("offer");
        setDetectedCampaignStep(1);
        sendCampaignMessage(
          "Zaczynamy od nowa — zadaj mi pytania potrzebne do zbudowania nowej oferty.",
          "offer"
        );
      },
      // Express init — skok do struktury kampanii (Krok 3) z pre-seedem goals + offer
      express_to_structure: () => {
        setCampaignPhase("main");
        setDetectedCampaignStep(3);
        sendCampaignMessage(t("campaignChat.expressToStructureMessage"), "main");
      },
      // Express init — user chce najpierw przejrzeć arkusz (idzie do offer-builder)
      express_review_offer: () => {
        if (onModuleSelect)
          onModuleSelect(
            "offer-builder",
            "Przejrzyj i ewentualnie popraw arkusz ofertowy wygenerowany przez Express init."
          );
      },
      // Welcome message — beginner clicks "let's start"
      start_intro: () => {
        setCampaignPhase("offer");
        setDetectedCampaignStep(1);
        sendCampaignMessage(
          "Zaczynamy! Jestem początkującym — prowadź mnie krok po kroku, prostym językiem. Zadawaj jedno pytanie na raz.",
          "offer"
        );
      },
      // Welcome message — user already has offer
      have_offer_ready: () => {
        setCampaignPhase("offer");
        setDetectedCampaignStep(1);
        sendCampaignMessage(
          "Mam już gotową ofertę — pomóż mi szybko ją wprowadzić. Zadaj minimum pytań, tylko najważniejsze.",
          "offer"
        );
      },
    };
    return handlers[optId];
  };

  // ─── Autopilot: backend fills draft for the user; we then jump to readiness ───
  const runAutopilot = async (preset: "broad" | "precise" | "local") => {
    if (!campaignDraftId || isAutopiloting) {
      // Autopilot wymaga draftu — wymusza najpierw co najmniej jedną wiadomość w fazie kampanii
      if (!campaignDraftId) {
        setCampaignMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: t("systemMessages.noDraftYet"),
            phase: "main",
          },
        ]);
      }
      return;
    }
    setIsAutopiloting(true);
    setCampaignMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: `Autopilot: ${preset === "broad" ? "szeroka grupa" : preset === "precise" ? "precyzyjna" : "lokalna"}`,
      },
    ]);
    try {
      const resp = await apiRequest(
        "POST",
        `/api/campaign-creator/draft/${campaignDraftId}/auto-fill`,
        {
          targetingPreset: preset,
        }
      );
      const summary = resp?.result;
      const summaryText = [
        t("systemMessages.autopilotTitle"),
        "",
        `**Targeting:** ${summary?.targetingLabel ?? preset}`,
        `**Budżet dzienny:** ${summary?.patch?.goals?.dailyBudget ?? "-"} zł`,
        `**Teksty:** ${summary?.generatedCopy?.primaryTexts?.length ?? 0} wariantów + ${summary?.generatedCopy?.headlines?.length ?? 0} nagłówków`,
        `**Formularz:** Imię + e-mail + telefon`,
        "",
        t("systemMessages.autopilotRemaining"),
      ].join("\n");
      setCampaignMessages((prev) => [
        ...prev,
        { role: "assistant", content: summaryText, phase: "main" },
      ]);
      setDetectedCampaignStep(6);
      await refreshReadiness(campaignDraftId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Autopilot się nie udał.";
      setCampaignMessages((prev) => [
        ...prev,
        { role: "assistant", content: `❌ ${msg}`, phase: "main" },
      ]);
    } finally {
      setIsAutopiloting(false);
    }
  };

  // ─── Refresh readiness when reaching step 6 ───
  useEffect(() => {
    if (detectedCampaignStep >= 6 && campaignDraftId) {
      void refreshReadiness(campaignDraftId);
    }
  }, [detectedCampaignStep, campaignDraftId]);

  const handleSend = async () => {
    if (!value.trim()) return;

    // If campaign chat is already active, send directly
    if (campaignChatActive) {
      sendCampaignMessage(value.trim());
      return;
    }

    setIsSending(true);
    setStrategyResult(null);
    const originalMessage = value.trim();
    try {
      const data = await apiRequest("POST", "/api/module-router", { message: originalMessage });

      // If campaign-creator detected, start inline chat immediately
      if (data.moduleId === "campaign-creator") {
        startCampaignChat(originalMessage);
        setIsSending(false);
        return;
      }

      setRouteResult({
        moduleId: data.moduleId,
        moduleName: data.moduleName,
        suggestion: data.suggestion,
        originalPrompt: originalMessage,
      });
    } catch {
      onSubmit(originalMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleRouteConfirm = () => {
    if (!routeResult) return;
    if (onModuleSelect) {
      onModuleSelect(routeResult.moduleId, routeResult.originalPrompt);
    }
    setRouteResult(null);
    setValue("");
  };

  const handleRouteCancel = () => {
    setRouteResult(null);
    setStrategyResult(null);
  };

  const handleSuggestionClick = (suggestion: ModuleSuggestion) => {
    // If clicking Kampanie, start inline chat
    if (suggestion.moduleId === "campaign-creator") {
      startCampaignChat(suggestion.prompt || "Chcę stworzyć kampanię reklamową");
      return;
    }
    if (onModuleSelect) {
      onModuleSelect(suggestion.moduleId, suggestion.prompt);
    }
  };

  const handleExitCampaignChat = () => {
    setCampaignChatActive(false);
    setCampaignMessages([]);
    setCampaignConvId(null);
    setOfferConvId(null);
    setCampaignPhase("offer");
    setSavedOfferIndices(new Set());
    setSavedKpiIndices(new Set());
    setDetectedCampaignStep(1);
    setCampaignDraftId(null);
    setDraftReadiness(null);
    setIsLaunchingCampaign(false);
    setMetaConnectionWarning(false);
    setValue("");
  };

  // ─── CAMPAIGN CHAT MODE ───
  if (campaignChatActive) {
    const lastMsg = campaignMessages[campaignMessages.length - 1];
    const lastOptions =
      lastMsg?.role === "assistant" ? parseCampaignOptions(lastMsg.content).options : [];

    return (
      <div className="flex flex-col w-full h-full bg-transparent text-white relative">
        {/* Background glows */}
        {!L && (
          <div className="fixed inset-0 w-screen h-screen overflow-hidden pointer-events-none z-0">
            <motion.div
              className="absolute w-[700px] h-[700px] bg-[#ef3e29]/20 rounded-full filter blur-[180px]"
              style={{ top: "-10%", left: "10%" }}
            />
            <motion.div
              className="absolute w-[600px] h-[600px] bg-[#ef3e29]/15 rounded-full filter blur-[160px]"
              style={{ bottom: "-5%", right: "5%" }}
            />
          </div>
        )}

        {/* Header bar */}
        <div className="relative z-10 border-b border-white/[0.06]">
          {/* Title row */}
          <div className="flex items-center gap-3 px-6 py-3">
            <button
              onClick={handleExitCampaignChat}
              className="p-2 rounded-lg hover:bg-white/[0.06] text-white/50 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-white">{t("campaignChat.title")}</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30">
                {t("campaignChat.beta")}
              </span>
              <span className="text-xs text-white/40 hidden sm:block">
                {t("campaignChat.subtitle")}
              </span>
            </div>
            <div className="ml-auto text-xs text-white/40 flex-shrink-0">
              {t("campaignChat.stepN", { current: detectedCampaignStep, total: 6 })}
            </div>
          </div>

          {/* Campaign progress bar */}
          <CampaignProgressBar currentStep={detectedCampaignStep} />

          {/* Meta connection preflight — blocks attention before user invests time */}
          {metaConnectionWarning && (
            <div className="px-6 py-3 bg-amber-500/[0.08] border-t border-amber-500/30 flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                <Rocket className="w-3.5 h-3.5 text-amber-300" />
              </div>
              <div className="flex-1 text-xs leading-snug text-amber-100/90">
                {t("campaignChat.metaWarning")}
              </div>
              <button
                onClick={() => onModuleSelect && onModuleSelect("settings", "Połącz Meta")}
                className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/40 border border-amber-500/40 text-amber-100 text-xs font-medium transition flex-shrink-0"
              >
                {t("campaignChat.connectNow")}
              </button>
            </div>
          )}

          {/* Safety reassurance — visible until launch step (hidden when Meta warning shown) */}
          {detectedCampaignStep < 6 && !metaConnectionWarning && (
            <div className="px-6 py-2.5 bg-emerald-500/[0.06] border-t border-emerald-500/20 flex items-start gap-2.5">
              <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs leading-snug">
                <span className="text-emerald-200 font-medium">
                  {t("campaignChat.safetyBannerTitle")}
                </span>
                <span className="text-emerald-200 dark:text-emerald-200/70">
                  {" "}
                  — {t("campaignChat.safetyBannerBody")}
                </span>
              </div>
            </div>
          )}

          {/* Express init banner — visible only on Krok 1, before user starts typing */}
          {!expressDismissed && campaignPhase === "offer" && detectedCampaignStep <= 1 && (
            <div className="px-6 py-3 bg-gradient-to-r from-orange-500/[0.08] to-red-500/[0.04] border-t border-orange-500/30">
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="w-4 h-4 text-orange-300" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-orange-200">
                  {t("campaignChat.expressBannerTitle")}
                </span>
                <span className="text-[11px] text-white/50">
                  {t("campaignChat.expressBannerSubtitle")}
                </span>
                <button
                  type="button"
                  onClick={() => setExpressDismissed(true)}
                  className="ml-auto text-[11px] text-white/40 hover:text-white/70"
                >
                  {t("campaignChat.expressDismiss")}
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={expressUrl}
                  onChange={(e) => setExpressUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !expressLoading) {
                      e.preventDefault();
                      runExpressInit();
                    }
                  }}
                  placeholder={t("campaignChat.expressInputPlaceholder")}
                  disabled={expressLoading}
                  className="flex-1 h-9 px-3 text-sm rounded-lg bg-[#030407] border border-white/[0.1] text-white placeholder:text-white/30 focus:outline-none focus:border-orange-400/50"
                />
                <button
                  type="button"
                  onClick={runExpressInit}
                  disabled={expressLoading || !expressUrl.trim()}
                  className="h-9 px-4 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 text-white text-xs font-semibold hover:from-orange-400 hover:to-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                >
                  {expressLoading
                    ? t("campaignChat.expressButtonLoading")
                    : t("campaignChat.expressButtonIdle")}
                </button>
              </div>
              {expressError && (
                <div className="mt-1.5 text-[11px] text-red-300">{expressError}</div>
              )}
              {expressLoading && (
                <div className="mt-1.5 text-[11px] text-white/50">
                  {t("campaignChat.expressProgressText")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Messages area */}
        <div
          ref={campaignScrollRef}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-5 relative z-10"
        >
          {campaignMessages.map((msg, i) => {
            const isLast = i === campaignMessages.length - 1;
            const { cleanContent, options } =
              msg.role === "assistant"
                ? parseCampaignOptions(msg.content)
                : { cleanContent: msg.content, options: [] as CampaignOption[] };

            const isOfferReady = msg.role === "assistant" && msg.content.includes("[OFFER_READY]");
            const displayContent = isOfferReady
              ? cleanContent.replace(/^\[OFFER_READY\]\s*/m, "").trimStart()
              : cleanContent;

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <Rocket className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-5 py-4",
                    msg.role === "user"
                      ? "bg-gradient-to-br from-red-600 to-red-700 text-white rounded-br-md shadow-lg shadow-red-600/10"
                      : "bg-white/[0.06] backdrop-blur-xl border border-white/[0.1] text-white rounded-tl-md"
                  )}
                >
                  {msg.role === "assistant" ? (
                    <div className="space-y-1">
                      <div
                        className={cn(
                          "text-sm leading-relaxed",
                          "[&_h2]:text-foreground dark:[&_h2]:text-white [&_h2]:font-semibold [&_h2]:text-base [&_h2]:mt-3 [&_h2]:mb-1",
                          "[&_h3]:text-muted-foreground dark:[&_h3]:text-white/80 [&_h3]:font-medium [&_h3]:mt-2 [&_h3]:mb-1",
                          "[&_strong]:text-foreground dark:[&_strong]:text-white [&_ul]:my-1 [&_li]:my-0.5 [&_p]:my-1",
                          "[&_em]:text-muted-foreground dark:[&_em]:text-white/60",
                          // Tables (GFM)
                          "[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
                          "[&_thead]:bg-white/[0.04]",
                          "[&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground dark:[&_th]:text-white [&_th]:border [&_th]:border-white/[0.08]",
                          "[&_td]:px-3 [&_td]:py-2 [&_td]:text-muted-foreground dark:[&_td]:text-white/80 [&_td]:border [&_td]:border-white/[0.06] [&_td]:align-top",
                          "[&_tr:nth-child(even)]:bg-white/[0.02]"
                        )}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
                      </div>
                      {/* Option buttons — only on the last assistant message */}
                      {options.length > 0 && isLast && !isCampaignSending && (
                        <div className="mt-4 pt-3 border-t border-white/[0.08] flex flex-wrap gap-2">
                          {options.map((opt) => (
                            <motion.button
                              key={opt.id}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => {
                                const handler = buildOptionHandler(opt.id);
                                if (handler) {
                                  void handler();
                                  return;
                                }
                                sendCampaignMessage(opt.label);
                              }}
                              className="px-4 py-2.5 rounded-xl text-sm font-medium bg-white/[0.06] hover:bg-[#ef3e29]/20 border border-white/[0.1] hover:border-[#ef3e29]/40 text-white/80 hover:text-white transition-all"
                            >
                              {opt.label}
                            </motion.button>
                          ))}
                        </div>
                      )}

                      {/* Save button + phase transition — only when offer document is ready */}
                      {isOfferReady && isLast && !isCampaignSending && (
                        <div className="mt-4 pt-3 border-t border-white/[0.08] flex flex-wrap items-center gap-2">
                          {savedOfferIndices.has(i) ? (
                            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs bg-emerald-950/40 border border-emerald-700/30 text-emerald-300">
                              <Check className="w-3.5 h-3.5" />
                              {t("campaignChat.offerSaved")}
                            </div>
                          ) : (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleSaveOffer(msg.content, i)}
                              className="px-4 py-2 rounded-xl text-sm font-medium bg-green-600/20 hover:bg-green-600/40 border border-green-500/30 hover:border-green-400/60 text-green-300 hover:text-green-100 transition-all"
                            >
                              {t("campaignChat.saveOffer")}
                            </motion.button>
                          )}
                          {campaignPhase === "offer" && (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={handleSwitchToCampaignPhase}
                              className="px-4 py-2 rounded-xl text-sm font-medium bg-[#ef3e29]/20 hover:bg-[#ef3e29]/40 border border-[#ef3e29]/30 hover:border-[#ef3e29]/60 text-[#ef3e29] hover:text-white transition-all flex items-center gap-1.5"
                            >
                              <Rocket className="w-3.5 h-3.5" />
                              {t("campaignChat.goToKpi")}
                            </motion.button>
                          )}
                        </div>
                      )}
                      {/* Step 4: inline ad graphics builder */}
                      {msg.phase === "main" && detectedCampaignStep === 4 && (
                        <div className="mt-4 -mx-5 -mb-4">
                          <div className="px-4 py-2.5 bg-purple-500/10 border-t border-b border-purple-500/20 flex items-center gap-2">
                            <Palette className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                            <span className="text-xs font-medium text-purple-300 flex-1">
                              {t("campaignChat.graphicsBuilder")}
                            </span>
                            <button
                              onClick={() => setGraphicsBuilderFullScreen(true)}
                              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-purple-300 hover:text-white hover:bg-purple-500/20 transition-all"
                              title={t("campaignChat.graphicsBuilderFullscreen")}
                            >
                              <Maximize2 className="w-3 h-3" />
                              {t("campaignChat.fullscreen")}
                            </button>
                          </div>
                          <div className="overflow-y-auto max-h-[70vh] bg-[#0a0c10]">
                            <AdGraphicsBuilder
                              contentType="ad"
                              campaignHandoff={{
                                adPrompt: buildCampaignAdPrompt(
                                  msg.content,
                                  clientOfferSheetData?.offerSheet?.title
                                ),
                                referenceImages: referencePhotos,
                                businessModel: campaignBusinessModel,
                                onGenerated: campaignDraftId
                                  ? async (url: string) => {
                                      try {
                                        await apiRequest(
                                          "POST",
                                          `/api/campaign-creator/draft/${campaignDraftId}/attach-creative`,
                                          {
                                            url,
                                            type: "image",
                                          }
                                        );
                                      } catch {
                                        // silent
                                      }
                                    }
                                  : undefined,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Goals phase: button to jump to Krok 3 (Struktura) once KPI is saved */}
                      {msg.phase === "goals" && isLast && !isCampaignSending && (
                        <div className="mt-3 pt-3 border-t border-white/[0.08] flex flex-wrap items-center gap-2">
                          {parseKpiBlock(msg.content) && !savedKpiIndices.has(i) && (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => {
                                const kpi = parseKpiBlock(msg.content);
                                if (kpi) saveKpiTargets(kpi, i);
                              }}
                              className="px-3 py-2 rounded-xl text-xs font-medium bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/30 hover:border-emerald-400/60 text-emerald-300 hover:text-emerald-100 transition-all"
                            >
                              {t("campaignChat.saveKpi")}
                            </motion.button>
                          )}
                          {savedKpiIndices.has(i) && (
                            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs bg-emerald-950/40 border border-emerald-700/30 text-emerald-300">
                              <Check className="w-3.5 h-3.5" />
                              {t("campaignChat.kpiSaved")}
                            </div>
                          )}
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleSwitchToStructurePhase}
                            className="px-4 py-2 rounded-xl text-sm font-medium bg-[#ef3e29]/20 hover:bg-[#ef3e29]/40 border border-[#ef3e29]/30 hover:border-[#ef3e29]/60 text-[#ef3e29] hover:text-white transition-all flex items-center gap-1.5"
                          >
                            <Rocket className="w-3.5 h-3.5" />
                            {t("campaignChat.goToStructure")}
                          </motion.button>
                        </div>
                      )}

                      {/* Save button for campaign-main phase (kroki 2-6) */}
                      {msg.phase === "main" &&
                        isLast &&
                        !isCampaignSending &&
                        detectedCampaignStep >= 2 && (
                          <div className="mt-3 pt-3 border-t border-white/[0.08] flex flex-wrap items-center gap-2">
                            {savedKpiIndices.has(i) ? (
                              <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs bg-emerald-950/40 border border-emerald-700/30 text-emerald-300">
                                <Check className="w-3.5 h-3.5" />
                                {t("campaignChat.kpiSavedAnalytics")}
                              </div>
                            ) : parseKpiBlock(msg.content) ? (
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                  const kpi = parseKpiBlock(msg.content);
                                  if (kpi) saveKpiTargets(kpi, i);
                                }}
                                className="px-3 py-2 rounded-xl text-xs font-medium bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/30 hover:border-emerald-400/60 text-emerald-300 hover:text-emerald-100 transition-all"
                              >
                                {t("campaignChat.saveKpi")}
                              </motion.button>
                            ) : (
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleSaveOffer(msg.content, i)}
                                className="px-3 py-2 rounded-xl text-xs font-medium bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.1] text-white/60 hover:text-white transition-all"
                              >
                                {t("campaignChat.saveStep", { n: detectedCampaignStep })}
                              </motion.button>
                            )}
                          </div>
                        )}
                    </div>
                  ) : (
                    <span className="text-sm">{msg.content}</span>
                  )}
                </div>
              </motion.div>
            );
          })}

          {/* Typing indicator */}
          {isCampaignSending && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 mr-3">
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.1] rounded-2xl rounded-tl-md px-5 py-4">
                <TypingDots />
              </div>
            </motion.div>
          )}

          {/* Autopilot indicator */}
          {isAutopiloting && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto max-w-2xl rounded-2xl border border-[#ef3e29]/30 bg-[#ef3e29]/10 px-5 py-4 flex items-center gap-3"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-600 to-orange-500 flex items-center justify-center flex-shrink-0 animate-pulse">
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <div className="text-sm text-white/90">
                <div className="font-medium">{t("campaignChat.autopilotRunning")}</div>
                <div className="text-xs text-white/60 mt-0.5">
                  {t("campaignChat.autopilotSubtext")}
                </div>
              </div>
            </motion.div>
          )}

          {/* Readiness panel — Step 6 / launch */}
          {detectedCampaignStep >= 6 && campaignDraftId && draftReadiness && (
            <div className="mx-auto max-w-2xl">
              <ReadinessPanel
                ready={draftReadiness.ready}
                missing={draftReadiness.missing}
                isLaunching={isLaunchingCampaign}
                onLaunch={launchCampaignFromDraft}
                onFix={(item) => {
                  if (item === "meta_connection" && onModuleSelect) {
                    onModuleSelect("settings", "Połącz Meta");
                    return;
                  }
                  sendCampaignMessage(`Pomóż mi uzupełnić: ${item}`);
                }}
              />
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="relative z-10 border-t border-white/[0.06] bg-gradient-to-t from-background via-background to-transparent dark:from-[#030407] dark:via-[#030407]/98 backdrop-blur-xl">
          {/* Always-visible "Help me" rescue button — shown after welcome, before launch step */}
          {campaignMessages.length > 1 && detectedCampaignStep < 6 && !isCampaignSending && (
            <div className="max-w-3xl mx-auto px-6 pt-3 pb-1 flex justify-center">
              <button
                type="button"
                onClick={() =>
                  sendCampaignMessage(
                    "Nie wiem co odpowiedzieć na to pytanie. Wybierz dla mnie najlepszą domyślną opcję, krótko wyjaśnij dlaczego, i przejdź dalej."
                  )
                }
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.1] hover:border-white/[0.2] text-white/60 hover:text-white/90 transition"
              >
                {t("campaignChat.helpMeButton")}
              </button>
            </div>
          )}
          <div className="max-w-3xl mx-auto px-6 py-4">
            <div className="relative backdrop-blur-2xl bg-white/[0.02] rounded-2xl border border-white/[0.05]">
              <div className="p-3">
                <textarea
                  ref={campaignInputRef}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={handleCampaignKeyDown}
                  placeholder={t("campaignChat.inputPlaceholder")}
                  disabled={isCampaignSending}
                  className={cn(
                    "w-full px-3 py-2",
                    "resize-none",
                    "bg-transparent",
                    "border-none",
                    "text-white/90 text-sm",
                    "focus:outline-none",
                    "placeholder:text-white/30",
                    "min-h-[40px] max-h-[120px]"
                  )}
                  style={{ overflow: "hidden" }}
                />
              </div>
              <div className="px-3 pb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Rocket className="w-4 h-4 text-[#ef3e29]/60" />

                  {/* Client selector for agency users */}
                  {isAgencyUser && myClients.length > 0 ? (
                    <div ref={clientDropdownRef} className="relative">
                      <button
                        type="button"
                        onClick={() => setIsClientDropdownOpen((prev) => !prev)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/[0.15] text-white/60 hover:text-white/80 transition-all"
                      >
                        <Users className="w-3 h-3" />
                        <span className="max-w-[140px] truncate">
                          {selectedClientId === "none"
                            ? t("clientSelector.selectClient")
                            : selectedClient?.name || t("clientSelector.clientLabel")}
                        </span>
                        <ChevronDown
                          className={cn(
                            "w-3 h-3 transition-transform",
                            isClientDropdownOpen && "rotate-180"
                          )}
                        />
                      </button>

                      {isClientDropdownOpen && (
                        <div className="absolute bottom-full mb-2 left-0 w-[260px] max-h-[240px] overflow-y-auto rounded-xl bg-[#0a0c10] border border-white/[0.1] shadow-2xl shadow-black/60 z-50">
                          <button
                            type="button"
                            onClick={() => {
                              setGlobalSelectedClient(null);
                              setIsClientDropdownOpen(false);
                            }}
                            className={cn(
                              "w-full text-left px-3 py-2.5 text-xs hover:bg-white/[0.06] transition-colors",
                              selectedClientId === "none" ? "text-[#ef3e29]" : "text-white/50"
                            )}
                          >
                            {t("clientSelector.noClient")}
                          </button>
                          {myClients.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setGlobalSelectedClient({
                                  id: c.id,
                                  userId: c.userId!,
                                  name: c.name,
                                });
                                setIsClientDropdownOpen(false);
                              }}
                              className={cn(
                                "w-full text-left px-3 py-2.5 text-xs hover:bg-white/[0.06] transition-colors flex items-center justify-between",
                                selectedClientId === c.id ? "text-[#ef3e29]" : "text-white/80"
                              )}
                            >
                              <span className="truncate">{c.name}</span>
                              {c.businessType && (
                                <span className="text-[10px] text-white/30 ml-2 flex-shrink-0">
                                  {c.businessType}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-white/30">
                      {t("campaignChat.campaignCreatorLabel")}
                    </span>
                  )}
                </div>
                <motion.button
                  type="button"
                  onClick={() => sendCampaignMessage(value.trim())}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isCampaignSending || !value.trim()}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2",
                    value.trim()
                      ? "bg-[#ef3e29] text-white shadow-lg shadow-[#ef3e29]/20"
                      : "bg-white/[0.05] text-white/40"
                  )}
                >
                  {isCampaignSending ? (
                    <LoaderIcon className="w-4 h-4 animate-[spin_2s_linear_infinite]" />
                  ) : (
                    <SendIcon className="w-4 h-4" />
                  )}
                  <span>{t("campaignChat.sendButton")}</span>
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col w-full items-center justify-center h-full bg-transparent p-6 relative ${L ? "text-gray-900" : "text-white"}`}
    >
      {/* Background glows — large, animated orbs (positioned fixed so they cover full viewport) */}
      {!L && (
        <div className="fixed inset-0 w-screen h-screen overflow-hidden pointer-events-none z-0">
          <motion.div
            className="absolute w-[700px] h-[700px] bg-[#ef3e29]/20 rounded-full filter blur-[180px]"
            animate={{
              x: ["-10%", "15%", "-5%"],
              y: ["-15%", "10%", "-10%"],
              scale: [1, 1.2, 0.95, 1],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            style={{ top: "-10%", left: "10%" }}
          />
          <motion.div
            className="absolute w-[600px] h-[600px] bg-[#ef3e29]/15 rounded-full filter blur-[160px]"
            animate={{
              x: ["10%", "-15%", "5%"],
              y: ["10%", "-10%", "15%"],
              scale: [1, 0.9, 1.15, 1],
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            style={{ bottom: "-5%", right: "5%" }}
          />
          <motion.div
            className="absolute w-[500px] h-[500px] bg-orange-500/15 rounded-full filter blur-[140px]"
            animate={{
              x: ["5%", "-10%", "10%"],
              y: ["-5%", "15%", "-10%"],
              scale: [0.9, 1.1, 0.95, 0.9],
            }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 4 }}
            style={{ top: "20%", right: "20%" }}
          />
          <motion.div
            className="absolute w-[450px] h-[450px] bg-rose-500/10 rounded-full filter blur-[120px]"
            animate={{
              x: ["-5%", "10%", "-8%"],
              y: ["8%", "-12%", "5%"],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut", delay: 6 }}
            style={{ bottom: "15%", left: "25%" }}
          />
        </div>
      )}

      <div className="w-full max-w-2xl mx-auto relative z-10">
        <motion.div
          className="relative z-10 space-y-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Greeting */}
          <div className="text-center space-y-3">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-block"
            >
              <h1 className="text-heading-1 font-extrabold tracking-tight pb-1">
                <LayoutGroup>
                  <motion.span
                    className={`inline bg-clip-text text-transparent bg-gradient-to-r ${L ? "from-gray-900 to-gray-600" : "from-white/90 to-white/40"}`}
                    layout
                    transition={{ type: "spring", damping: 30, stiffness: 400 }}
                  >
                    {t("greeting.todayTime")}{" "}
                  </motion.span>
                  <TextRotate
                    texts={t("greeting.rotatingWords", { returnObjects: true }) as string[]}
                    mainClassName="inline-flex px-3 py-0.5 bg-[#ef3e29]/20 backdrop-blur-xl overflow-hidden rounded-xl justify-center align-bottom shadow-[0_0_30px_rgba(239,62,41,0.15),inset_0_1px_0_rgba(255,255,255,0.06)] border border-[#ef3e29]/30"
                    elementLevelClassName="bg-clip-text text-transparent bg-gradient-to-b from-[#ff8a7a] via-[#ef3e29] to-[#d42a1a]"
                    staggerFrom="last"
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "-120%" }}
                    staggerDuration={0.025}
                    splitLevelClassName="overflow-hidden pb-0.5"
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    rotationInterval={3500}
                  />
                  <motion.span
                    className={`inline bg-clip-text text-transparent bg-gradient-to-r ${L ? "from-gray-900 to-gray-600" : "from-white/70 to-white/40"}`}
                    layout
                    transition={{ type: "spring", damping: 30, stiffness: 400 }}
                  >
                    , {displayName}!
                  </motion.span>
                </LayoutGroup>
              </h1>
              <motion.div
                className="h-px bg-gradient-to-r from-transparent via-[#ef3e29]/30 to-transparent"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: "100%", opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
            </motion.div>
            <motion.p
              className={`text-sm ${L ? "text-gray-500" : "text-white/40"}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {t("greeting.subtitle")}
            </motion.p>
          </div>

          {/* Input box */}
          <motion.div
            className={`relative backdrop-blur-2xl rounded-2xl shadow-2xl ${L ? "bg-white border border-gray-200" : "bg-white/[0.02] border border-white/[0.05]"}`}
            initial={{ scale: 0.98 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="p-4">
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  adjustHeight();
                }}
                onKeyDown={handleKeyDown}
                placeholder={t("input.placeholder")}
                className={cn(
                  "w-full px-4 py-3",
                  "resize-none",
                  "bg-transparent",
                  "border-none",
                  "text-sm",
                  L
                    ? "text-gray-900 placeholder:text-gray-400"
                    : "text-white/90 placeholder:text-white/20",
                  "focus:outline-none",
                  "min-h-[56px]"
                )}
                style={{ overflow: "hidden" }}
              />
            </div>

            <div
              className={`p-4 border-t flex items-center justify-between gap-4 ${L ? "border-gray-200" : "border-white/[0.05]"}`}
            >
              <div className="flex items-center gap-3">
                <img
                  src="https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Viral+Partners+App+Logo.png"
                  alt="VP"
                  className="w-5 h-5 object-contain rounded"
                />

                {/* Client selector for agency users — always visible on welcome screen */}
                {isAgencyUser && myClients.length > 0 ? (
                  <div ref={clientDropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setIsClientDropdownOpen((prev) => !prev)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/[0.15] text-white/60 hover:text-white/80 transition-all"
                    >
                      <Users className="w-3 h-3" />
                      <span className="max-w-[160px] truncate">
                        {selectedClientId === "none"
                          ? t("clientSelector.selectClient")
                          : selectedClient?.name || t("clientSelector.clientLabel")}
                      </span>
                      <ChevronDown
                        className={cn(
                          "w-3 h-3 transition-transform",
                          isClientDropdownOpen && "rotate-180"
                        )}
                      />
                    </button>

                    {isClientDropdownOpen && (
                      <div className="absolute bottom-full mb-2 left-0 w-[280px] max-h-[280px] overflow-y-auto rounded-xl bg-[#0a0c10] border border-white/[0.1] shadow-2xl shadow-black/60 z-50">
                        <div className="px-3 py-2 border-b border-white/[0.06]">
                          <span className="text-[10px] uppercase tracking-wider text-white/30">
                            {t("clientSelector.clientsHeader")}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setGlobalSelectedClient(null);
                            setIsClientDropdownOpen(false);
                          }}
                          className={cn(
                            "w-full text-left px-3 py-2.5 text-xs hover:bg-white/[0.06] transition-colors",
                            selectedClientId === "none" ? "text-[#ef3e29]" : "text-white/50"
                          )}
                        >
                          {t("clientSelector.noClient")}
                        </button>
                        {myClients.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              setGlobalSelectedClient({
                                id: c.id,
                                userId: c.userId!,
                                name: c.name,
                              });
                              setIsClientDropdownOpen(false);
                            }}
                            className={cn(
                              "w-full text-left px-3 py-2.5 text-xs hover:bg-white/[0.06] transition-colors flex items-center justify-between",
                              selectedClientId === c.id ? "text-[#ef3e29]" : "text-white/80"
                            )}
                          >
                            <span className="truncate">{c.name}</span>
                            {c.businessType && (
                              <span className="text-[10px] text-white/30 ml-2 flex-shrink-0">
                                {c.businessType}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className={`text-xs ${L ? "text-gray-400" : "text-white/30"}`}>
                    Viral Partners
                  </span>
                )}
              </div>

              <motion.button
                type="button"
                onClick={handleSend}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSending || !value.trim()}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                  "flex items-center gap-2",
                  value.trim()
                    ? "bg-[#ef3e29] text-white shadow-lg shadow-[#ef3e29]/20"
                    : L
                      ? "bg-gray-100 text-gray-400"
                      : "bg-white/[0.05] text-white/40"
                )}
              >
                {isSending ? (
                  <LoaderIcon className="w-4 h-4 animate-[spin_2s_linear_infinite]" />
                ) : (
                  <SendIcon className="w-4 h-4" />
                )}
                <span>{t("input.sendButton")}</span>
              </motion.button>
            </div>
          </motion.div>

          {/* AI Route Confirmation (non-campaign modules only) */}
          <AnimatePresence>
            {routeResult && routeResult.moduleId !== "campaign-creator" && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.97 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="backdrop-blur-2xl bg-white/[0.04] rounded-2xl border border-white/[0.08] p-5 space-y-4"
              >
                <p className="text-sm text-white/70 leading-relaxed">{routeResult.suggestion}</p>
                <div className="flex items-center gap-3">
                  <motion.button
                    onClick={handleRouteConfirm}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#ef3e29] hover:bg-[#d63520] text-white text-sm font-medium transition-colors shadow-lg shadow-[#ef3e29]/20"
                  >
                    <Check className="w-4 h-4" />
                    {routeResult.moduleName}
                  </motion.button>
                  <button
                    onClick={handleRouteCancel}
                    className="p-2.5 rounded-xl text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Module suggestions */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {visibleSuggestions.map((suggestion, index) => (
              <motion.button
                key={suggestion.moduleId}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all relative group border hover:border-[#ef3e29]/30 ${L ? "bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900 border-gray-200" : "bg-white/[0.02] hover:bg-white/[0.05] text-white/60 hover:text-white/90 border-white/[0.05]"}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.06 }}
              >
                {suggestion.icon}
                <span>{t(suggestion.labelKey)}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Full-screen Ad Graphics Builder modal */}
      <AnimatePresence>
        {graphicsBuilderFullScreen && detectedCampaignStep === 4 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-3 border-b border-white/[0.08] bg-[#0a0c10]">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-white">
                  {t("campaignChat.graphicsBuilderFullscreen")}
                </span>
              </div>
              <button
                onClick={() => setGraphicsBuilderFullScreen(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/[0.08] transition-all"
              >
                <Minimize2 className="w-3.5 h-3.5" />
                {t("campaignChat.minimize")}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-[#0a0c10]">
              {(() => {
                const lastStep4Msg = [...campaignMessages]
                  .reverse()
                  .find((m) => m.role === "assistant" && m.phase === "main");
                const adPrompt = lastStep4Msg
                  ? buildCampaignAdPrompt(
                      lastStep4Msg.content,
                      clientOfferSheetData?.offerSheet?.title
                    )
                  : "";
                return (
                  <AdGraphicsBuilder
                    contentType="ad"
                    campaignHandoff={{
                      adPrompt,
                      referenceImages: referencePhotos,
                      onGenerated: campaignDraftId
                        ? async (url: string) => {
                            try {
                              await apiRequest(
                                "POST",
                                `/api/campaign-creator/draft/${campaignDraftId}/attach-creative`,
                                { url, type: "image" }
                              );
                            } catch {
                              // silent
                            }
                          }
                        : undefined,
                    }}
                  />
                );
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center ml-1">
      {[1, 2, 3].map((dot) => (
        <motion.div
          key={dot}
          className="w-1.5 h-1.5 bg-white/90 rounded-full mx-0.5"
          initial={{ opacity: 0.3 }}
          animate={{
            opacity: [0.3, 0.9, 0.3],
            scale: [0.85, 1.1, 0.85],
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: dot * 0.15,
            ease: "easeInOut",
          }}
          style={{ boxShadow: "0 0 4px rgba(239, 62, 41, 0.3)" }}
        />
      ))}
    </div>
  );
}
